import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useResizeObserver } from 'usehooks-ts';

import { Modal, Table, Tag, Form, Input, InputNumber, Select, Flex, Space, Checkbox, notification, Tooltip, Switch } from 'antd';
import { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload';
import type { GetRef } from 'antd';

import {
    LoadingOutlined,
    ReloadOutlined,
    EditOutlined,
    AppstoreAddOutlined,
    QuestionCircleOutlined,
    CheckCircleFilled,
    CheckOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    FormOutlined,
    DeleteOutlined,
    SwapOutlined,
} from '@ant-design/icons';

import UploadDocument from '@components/upload-document/upload-document';
import ConsignmentCard from '@components/consignment-card/consignment-card';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';
import FormHeader from '@controls/form-header/form-header';
import TariffCard from '@controls/tariff-card/tariff-card';
import Comment from '@controls/comment/comment';

import BillModal, { IBillParams } from '@flc/components/logistics/import/bills/bill-modal';
import Packages from '@flc/components/logistics/import/bills/packages';
import TransferBoxes from '@flc/components/transfer-boxes/transfer-boxes';

import { error, exception, securityRestriction } from '@extensions/notification';

import { userLoaded, setFilter } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { delayAction, toFinanceString, getEnumList, getDate } from '@extensions/utils';
import { serverFetch } from '@flc/server';

import { IUserSession } from '@entities/user-session';
import { IBill } from '@entities/bill';
import { IBillFilter } from '@entities/bill-filter';
import { IBillPackage } from '@entities/bill-package';
import { ICity } from '@entities/city';
import { IConsigneeTariffSetting } from '@entities/consignee-tariff-setting';
import { ITruck } from '@entities/truck';
import { IUser } from '@entities/user';
import { ICountry } from '@entities/country';
import { ICargoCarrier } from '@entities/cargo-carrier';
import { IItem } from '@entities/item';
import { IDeleteBillParams } from '@entities/delete-bill-params';
import { ITradingPlatform } from '@entities/trading-platform';
import { ICountryItem } from '@entities/country-item';

import { Permission, hasPermission } from '@enums/permission';
import { UnitType } from '@enums/unit-type';
import { WeightType } from '@enums/weight-type';
import { UserType } from '@enums/user-type';
import { IEnumItem } from '@enums/enum-item';
import { enumLabel as truckStatusLabel } from '@enums/truck-status';
import { BillStatus, enumLabel as billStatusLabel } from '@enums/bill-status';
import { DocumentType } from '@enums/document-type';
import { DocumentReferenceType } from '@enums/document-reference-type';

import { TruckIcon, AirplaneIcon, CalculatorIcon, PenIcon } from '@icons/index';

import './bills.css';

dayjs.extend(utc);

type TableRef = GetRef<typeof Table>;

const filterContext: string = 'ImportTariffs';

export interface IConsignmentTotals {
    totalBoxQty: number;
    totalVolumeWeight: number;
    totalPriceUsd: number;
    totalFlightPriceUsd?: number;
    totalDiscountUsd?: number;
}

const Bills = () => {
    const { TextArea } = Input;

    const { truckId } = useParams();

    const initFilter: IBillFilter = {
        truckIds: [truckId as string],
        includeDocuments: true,
    };

    const Buffer = require('buffer/').Buffer;

    const tableRef = useRef<TableRef>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [deletelForm] = Form.useForm();
    const [bulkTariffForm] = Form.useForm();
    const [awbForm] = Form.useForm();

    const setBulkCityRequired = Form.useWatch('setCityRequired', bulkTariffForm);

    const [api, contextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IBillFilter>((s) => s.filters[filterContext]);

    const [bills, setBills] = useState<Array<IBill>>([]);
    const [currentBill, setCurrentBill] = useState<IBill>();

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [bulkTariffOpen, setBulkTariffOpen] = useState<boolean>(false);
    const [editTariffModalOpen, setEditTariffModalOpen] = useState<boolean>(false);
    const [awbOpen, setAwbOpen] = useState<boolean>(false);

    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [openPackageModal, setOpenPackageModal] = useState<boolean>(false);

    const [packages, setPackages] = useState<Array<IBillPackage>>([]);
    const [truck, setTruck] = useState<ITruck>();
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [tariffSettings, setTariffSettings] = useState<Array<IConsigneeTariffSetting>>([]);
    const [contractors, setContractors] = useState<Array<IUser>>([]);
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [airCargoCarriers, setAirCargoCarriers] = useState<Array<ICargoCarrier>>([]);
    const [items, setItems] = useState<Array<IItem>>([]);
    const [countryItems, setCountryItems] = useState<Array<ICountryItem>>([]);

    const [platforms, setPlatforms] = useState<Array<ITradingPlatform>>([]);

    const [lastSelectedKey, setLastSelectedKey] = useState<React.Key>();
    const [consignmentTotals, setConsignmentTotals] = useState<IConsignmentTotals>();
    const [processedTotals, setProcessedTotals] = useState<IConsignmentTotals>({
        totalBoxQty: 0,
        totalVolumeWeight: 0,
        totalPriceUsd: 0,
        totalFlightPriceUsd: 0,
        totalDiscountUsd: 0,
    });
    const [statuses] = useState<Array<IEnumItem>>(getEnumList(BillStatus, billStatusLabel));
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [openTransferModal, setOpenTransferModal] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;

        const fetchData = async () => {
            bulkTariffForm.resetFields();

            setOpenPackageModal(false);
            setBulkTariffOpen(false);
            setEditTariffModalOpen(false);

            setLoading(true);

            setBills([]);
            setCities([]);
            setSelectedIds([]);
            setCurrentBill(undefined);

            const promises = [
                await serverFetch(`trucks/${truckId}`, { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения машины', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`consignees/tariffsettings`, { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения настроек тарифа грузополучателей', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('users', { method: 'GET', queryParams: { types: UserType.Supplier, isActiveOnly: true } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения поставщиков', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('bills', { method: 'GET', queryParams: filter })
                    .then((data: Array<IBill>) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения тарифов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`bills/consignmenttotals`, { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения общего количества груза', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('countries', { method: 'GET', queryParams: { withTransportOnly: true } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('cargocarriers/air', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения авиаперевозчиков', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('items', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch(`tradingplatforms`, { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения торговых площадок', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('countries/items', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setTruck(result[0][0]);
                setCities(result[0][1]);
                setTariffSettings(result[0][2]);
                setContractors(result[0][3]);
                setBills(result[0][4]);
                setConsignmentTotals(result[0][5]);
                setCountries(result[0][6].filter((c: ICountry) => c.generateTariff));
                setAirCargoCarriers(result[0][7]);
                setItems(result[0][8]);
                setPlatforms(result[0][9]);
                setCountryItems(result[0][10]);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter || !filter.truckIds?.find((t) => t === truckId)) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    useEffect(() => {
        if (!loading && lastSelectedKey) {
            tableRef.current?.scrollTo({ key: lastSelectedKey });
            setLastSelectedKey(undefined);
        }
    }, [loading]);

    useEffect(() => {
        if (!bills) return;

        const total: IConsignmentTotals = {
            totalBoxQty: 0,
            totalVolumeWeight: 0,
            totalPriceUsd: 0,
            totalFlightPriceUsd: 0,
            totalDiscountUsd: 0,
        };

        bills.map((b) => {
            if (b.unitType == UnitType.Weight) {
                total.totalBoxQty += b.qty || 0;
                total.totalVolumeWeight += b.volumeWeight || 0;

                if (b.cargoCarrierId) {
                    const setting = tariffSettings.find((s) => s.consigneeId == b?.consigneeId && s.countryId == b?.countryId);
                    const weight: number = setting && setting.weightType == WeightType.Gross ? b.grossWeight || 0 : b.volumeWeight || 0;
                    total.totalFlightPriceUsd = (total.totalFlightPriceUsd ?? 0) + (b.flightPrice ?? 0) * weight;
                }
            } else if (b.unitType == UnitType.Pallet) {
                if (b.packages && b.packages.length > 0) {
                    b.packages.map((p) => (total.totalBoxQty += p.boxQty || 0));
                } else {
                    total.totalBoxQty += Math.ceil(b.adjustmentQty || 0);
                }
            } else if (b.unitType == UnitType.Cart) {
                total.totalBoxQty += Math.ceil(b.adjustmentQty || 0);
            }

            total.totalPriceUsd += b.totalPrice || 0;
            total.totalDiscountUsd = (total.totalDiscountUsd ?? 0) + (b.discount ?? 0);
        });

        setProcessedTotals(total);
    }, [bills]);

    useEffect(() => {
        if (currentBill && !!currentBill.packages?.length) setPackages(currentBill.packages);
        else setPackages([]);
    }, [currentBill]);

    const onTariffUpdate = (entity: IBillParams) => {
        entity.billIds = selectedIds as Array<string>;

        serverFetch(`bills/tariff`, { method: 'PUT', bodyData: entity })
            .then(() => {
                setLastSelectedKey(selectedIds[0]);

                setEditTariffModalOpen(false);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка изменения тарифа', ex, () => d(userLoaded(undefined)));
            });
    };

    const onAwbTariffUpdate = (entity: IBill) => {
        if (!currentBill) return;

        serverFetch(`bills/awbtariff`, { method: 'PUT', bodyData: entity })
            .then(() => {
                awbForm.resetFields();

                setAwbOpen(false);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка изменения тарифа', ex, () => d(userLoaded(undefined)));
            });
    };

    const onCalculate = () => {
        setLoading(true);

        const boxGroupIds: Array<string> = [];
        selectedIds.map((id) => {
            const bill = bills.find((t) => t.id === id);
            if (bill && bill.boxGroupId) boxGroupIds.push(bill.boxGroupId);
        });
        serverFetch(`bills/${truckId}/calculate`, { method: 'POST', bodyData: boxGroupIds })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка расчета тарифов', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSavePackages = () => {
        setLoading(true);

        const entities = packages.filter((p) => p.templateId && !!p.boxQty);

        serverFetch(`bills/${currentBill?.id}/packages`, { method: 'POST', bodyData: entities })
            .then(() => {
                setOpenPackageModal(false);
                setRefreshRequired(true);
                setCurrentBill(undefined);
                setSelectedIds([]);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка добавления коробок', ex, () => d(userLoaded(undefined)));
            });
    };

    const onComplete = () => {
        setLoading(true);

        serverFetch(`bills/complete`, { method: 'POST', bodyData: selectedIds })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка проведения тарифов', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDelete = (data: IDeleteBillParams) => {
        if (selectedIds.length <= 0) return;

        data.ids = selectedIds;

        serverFetch(`bills`, { method: 'DELETE', bodyData: data })
            .then(() => {
                deletelForm.resetFields();
                setOpenDeleteModal(false);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления счета', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Рассчитать',
                        key: 'calculate',
                        disabled: loading,
                        type: 'primary',
                        icon: <CalculatorIcon />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTransportBill)) {
                                securityRestriction(api, [Permission.ManageTransportBill]);
                                return;
                            }

                            modal.confirm({
                                title:
                                    selectedIds.length <= 0
                                        ? `Рассчитать базовый тариф для новых маркировок?`
                                        : 'Сбросить выбранные строки к базовым значениям тарифа?',
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onCalculate();
                                },
                            });
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !selectedIds || selectedIds.length <= 0,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (
                                !hasPermission(userSession.permissions, Permission.ManageTransportBill) &&
                                !hasPermission(userSession.permissions, Permission.EditTransportBill)
                            ) {
                                securityRestriction(api, [Permission.ManageTransportBill, Permission.EditTransportBill]);
                                return;
                            }

                            if (selectedIds.length === 1) {
                                setEditTariffModalOpen(true);
                            } else {
                                setBulkTariffOpen(true);
                            }
                        },
                    },
                    {
                        label: 'AWB',
                        key: 'awb',
                        disabled: loading || !currentBill || !currentBill.awbNumber,
                        icon: <AirplaneIcon style={{ color: '#000000' }} />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTransportBill)) {
                                securityRestriction(api, [Permission.ManageTransportBill]);
                                return;
                            }

                            awbForm.setFieldsValue({
                                awbNumber: currentBill?.awbNumber,
                                flightPrice: currentBill?.flightPrice,
                            });

                            setAwbOpen(true);
                        },
                    },
                    {
                        label: 'Коробки',
                        key: 'boxes',
                        disabled: loading || !currentBill || currentBill?.unitType != UnitType.Pallet,
                        icon: <AppstoreAddOutlined />,
                        onClick: () => {
                            if (
                                !hasPermission(userSession.permissions, Permission.ManageTransportBill) &&
                                !hasPermission(userSession.permissions, Permission.EditTransportBill)
                            ) {
                                securityRestriction(api, [Permission.ManageTransportBill, Permission.EditTransportBill]);
                                return;
                            }

                            setOpenPackageModal(true);
                        },
                    },
                    {
                        label: 'Перестановка',
                        key: 'transfer',
                        disabled: !currentBill,
                        icon: <SwapOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.TransferBoxes)) {
                                securityRestriction(api, [Permission.TransferBoxes]);
                                return;
                            }

                            setOpenTransferModal(true);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: selectedIds.length <= 0,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            modal.confirm({
                                title: `Удалить выбранные счета?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    setOpenDeleteModal(true);
                                },
                            });
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Провести',
                        key: 'completed',
                        disabled: selectedIds.length <= 0,
                        icon: <CheckOutlined />,
                        type: 'primary',
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTransportBill)) {
                                securityRestriction(api, [Permission.ManageTransportBill]);
                                return;
                            }

                            modal.confirm({
                                title: `Провести выбранные тарифы?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onComplete();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <InputNumber
                            style={{ width: 150 }}
                            key='consigneeCode'
                            placeholder='ID клиента'
                            value={filter?.consigneeCode}
                            onChange={(value) => {
                                d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='markingCode'
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='city'
                            placeholder='Город доставки'
                            value={filter?.cityIds}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={cities.map((s) => {
                                return { value: s.id, label: s.name };
                            })}
                        />,
                        <Input
                            style={{ width: 230 }}
                            key='legalName'
                            placeholder='Контрагент'
                            value={filter?.legalName}
                            onChange={(data) => {
                                d(setFilter({ ...filter, legalName: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            maxTagCount='responsive'
                            key='contractor'
                            placeholder='Поставщик'
                            value={filter?.contractorIds}
                            size='middle'
                            mode='multiple'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, contractorIds: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={contractors.map((c) => {
                                return { value: c.id, label: c.login };
                            })}
                        />,
                        <Select
                            key='country'
                            placeholder='Страна'
                            mode='multiple'
                            maxTagCount='responsive'
                            value={filter?.countryIds}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, countryIds: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={countries.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Select
                            key='item'
                            placeholder='Продукт'
                            mode='multiple'
                            maxTagCount='responsive'
                            value={filter?.itemIds}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, itemIds: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={items.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Select
                            key='cargo'
                            placeholder='Авиаперевозчик'
                            mode='multiple'
                            maxTagCount='responsive'
                            value={filter?.cargoCarrierIds}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, cargoCarrierIds: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={airCargoCarriers.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='awbNumber'
                            placeholder='AWB'
                            value={filter?.awbNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='tradingPlatform'
                            placeholder='Торговая площадка'
                            value={filter?.tradingPlatform}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, tradingPlatform: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={platforms.map((c) => {
                                return { value: c.name, label: c.name };
                            })}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус счета'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 250 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                        <Form.Item
                            key='withFlightOnly'
                            label={
                                <Tooltip title='Только с перелетом'>
                                    <Flex align='center'>
                                        <AirplaneIcon style={{ color: '#000000' }} />
                                    </Flex>
                                </Tooltip>
                            }
                            style={{ margin: 0 }}
                        >
                            <Switch
                                checked={filter?.withFlightOnly}
                                onChange={(value: any) => {
                                    d(setFilter({ ...filter, withFlightOnly: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = bills.find((o) => o.id == selectedRowKeys[0]);
            setCurrentBill(entity);
        } else {
            setCurrentBill(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<IBill> = [
            {
                title: 'ID',
                width: 60,
                align: 'center',
                render: (_, record) => {
                    return (
                        <>
                            <div>{record.consigneeCode}</div>
                            {record.fromPlatina && <Tag color='#000000'>#PL</Tag>}
                        </>
                    );
                },
            },
            {
                title: '',
                width: 400,
                render: (_: any, record) => {
                    let comment: string = '';
                    if (record.consignmentComment) {
                        comment = 'Поставка:' + '\n' + record.consignmentComment + '\n\n';
                    }

                    if (record.comment) {
                        comment += 'Счет:' + '\n' + record.comment;
                    }

                    return (
                        <Form colon={false} labelCol={{ xs: 8 }} wrapperCol={{ xs: 16 }}>
                            <Form.Item label='Маркировка' style={{ marginBottom: 0 }}>
                                <span style={{ fontWeight: 600 }}>{record.markingCode}</span>
                            </Form.Item>
                            <Form.Item label='Контрагент' style={{ marginBottom: 0 }}>
                                <span style={{ fontWeight: 600 }}>{record.legalName}</span>
                            </Form.Item>
                            <Form.Item label='Расчетный город' style={{ marginBottom: 0 }}>
                                <span style={{ fontWeight: 600 }}>{record.cityName}</span>
                            </Form.Item>
                            {record.contractorName && (
                                <Form.Item label='Поставщик' style={{ marginBottom: 0 }}>
                                    <span style={{ fontWeight: 600 }}>{record.contractorName}</span>
                                </Form.Item>
                            )}

                            <Form.Item label='Номер' style={{ marginBottom: 0 }}>
                                <Flex align='center' gap='small'>
                                    <span style={{ fontWeight: 600 }}>{record.fullNumber}</span>

                                    {(record.status == BillStatus.Completed ||
                                        record.status == BillStatus.ToVerify ||
                                        record.status == BillStatus.Paid ||
                                        record.status == BillStatus.Abandoned) && (
                                        <Tooltip
                                            placement='top'
                                            title={
                                                record.status == BillStatus.Completed ||
                                                record.status === BillStatus.Paid ||
                                                record.status == BillStatus.Abandoned
                                                    ? 'Расчет Проведен'
                                                    : 'Счет ожидает проверки'
                                            }
                                            color={
                                                record.status == BillStatus.Completed ||
                                                record.status === BillStatus.Paid ||
                                                record.status == BillStatus.Abandoned
                                                    ? 'var(--main-green)'
                                                    : 'darkorange'
                                            }
                                        >
                                            <>
                                                {(record.status == BillStatus.Completed ||
                                                    record.status === BillStatus.Paid ||
                                                    record.status == BillStatus.Abandoned) && (
                                                    <CheckCircleOutlined
                                                        style={{
                                                            color: 'var(--main-green)',
                                                            fontSize: 18,
                                                        }}
                                                    />
                                                )}

                                                {record.status == BillStatus.ToVerify && (
                                                    <Tooltip placement='top' title='Счет ожидает проверки' color='#f6546a'>
                                                        <WarningOutlined
                                                            style={{
                                                                color: '#f6546a',
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </>
                                        </Tooltip>
                                    )}

                                    {record.isChanged && (
                                        <Tooltip title={`Счет изменен ${getDate(record.changedOn)}`}>
                                            <div>
                                                <PenIcon style={{ color: 'var(--main-green)' }} />
                                            </div>
                                        </Tooltip>
                                    )}

                                    {comment && <Comment comment={comment} />}
                                </Flex>
                            </Form.Item>
                        </Form>
                    );
                },
            },
            {
                title: '',
                align: 'center',
                width: 375,
                render: (_, record) => {
                    let diffQty: number = 0;
                    let useAdjustmentQty: boolean = false;

                    if (record.qty && record.unitType != UnitType.Weight) {
                        if (record.packages && record.packages.length > 0) {
                            let actualQty: number = 0;

                            if (record.packages) {
                                record.packages.map((p) => {
                                    actualQty += p.boxQty || 0;
                                });
                            }

                            diffQty = actualQty - record.qty;
                        } else {
                            useAdjustmentQty = true;
                        }
                    }

                    const country = countries.find((c) => c.id == record.countryId);
                    if (!country) return;

                    return (
                        <>
                            <ConsignmentCard
                                country={country}
                                api={api}
                                items={[
                                    {
                                        itemName: record.itemName,
                                        itemColor: record.itemColor,
                                        qty: record.qty,
                                        diffQty: diffQty,
                                        useAdjustmentQty: useAdjustmentQty,
                                        adjustmentQty: record.adjustmentQty,
                                        loadingOn: record.billDate,
                                        awbNumber: record.awbNumber,
                                        cargoCarrierName: record.cargoCarrierName,
                                    },
                                ]}
                                serverFetch={serverFetch}
                            />

                            <Form.Item
                                wrapperCol={{ span: 24 }}
                                style={{ textAlign: 'left', marginTop: 10, border: '1px solid #d9d9d9', borderRadius: 5, padding: 5 }}
                            >
                                <UploadDocument
                                    documentType={DocumentType.Invoice}
                                    referenceId={record.id}
                                    referenceType={DocumentReferenceType.Bill}
                                    files={record.documents}
                                    api={api}
                                    onChange={(fileList: Array<UploadFile>) => {
                                        const tmpBills = [...bills];
                                        const bill = tmpBills.find((t) => t.id == record.id);
                                        if (bill) {
                                            bill.documents = fileList;

                                            setBills(tmpBills);
                                        }
                                    }}
                                    serverFetch={serverFetch}
                                />
                            </Form.Item>
                        </>
                    );
                },
            },
            {
                title: 'Вес',
                width: 180,
                align: 'center',
                render: (_: any, record) => {
                    if (record.unitType !== UnitType.Weight) return '';

                    const setting = tariffSettings.find((s) => s.consigneeId == record?.consigneeId && s.countryId == record?.countryId);

                    return (
                        <Form colon={false} labelCol={{ xs: 12 }} wrapperCol={{ xs: 12 }}>
                            <Form.Item label='Брутто' style={{ marginBottom: 0 }}>
                                {record.grossWeight && (
                                    <Flex justify='start' style={{ fontWeight: 600 }}>
                                        {record.grossWeight} <span>кг</span>
                                        {setting && setting.weightType == WeightType.Gross && (
                                            <Tooltip title='Расчетный вес'>
                                                <CheckCircleFilled style={{ color: '#FFDD2D', marginLeft: 5 }} />
                                            </Tooltip>
                                        )}
                                    </Flex>
                                )}
                            </Form.Item>
                            <Form.Item label='Объемный'>
                                {record.volumeWeight && (
                                    <Flex justify='start' style={{ fontWeight: 600 }}>
                                        {record.volumeWeight} <span>кг</span>
                                        {(!setting || setting.weightType == WeightType.Volume) && (
                                            <Tooltip title='Расчетный вес'>
                                                <CheckCircleFilled style={{ color: '#FFDD2D', marginLeft: 5 }} />
                                            </Tooltip>
                                        )}
                                    </Flex>
                                )}
                            </Form.Item>
                        </Form>
                    );
                },
            },
            {
                title: 'Тариф',
                width: 100,
                align: 'center',
                render: (_: any, record) => {
                    if (!record.boxGroupId) return;

                    return (
                        <TariffCard
                            cargoCarrierId={record.cargoCarrierId}
                            flightPrice={record.flightPrice}
                            flightPriceRequired={!!record.cargoCarrierId}
                            truckPrice={record.truckPrice}
                            truckPriceRequired={true}
                        />
                    );
                },
            },
            {
                title: 'Упаковка',
                width: 280,
                onCell: () => ({
                    style: {
                        padding: 0,
                    },
                }),
                render: (_: any, record) => {
                    if (record.unitType == UnitType.Cart) {
                        return (
                            <Tag color='#780606' style={{ fontSize: 14, marginLeft: 5, marginTop: 5, marginBottom: 5 }}>
                                Телега
                            </Tag>
                        );
                    } else if (record.unitType == UnitType.Pallet) {
                        return record.packages?.map((p) => {
                            return (
                                <Tooltip
                                    title={p?.usePrice === false ? 'Рассчет стоимости вручную' : ''}
                                    color={p?.usePrice === false ? 'var(--main-red)' : ''}
                                >
                                    <Tag
                                        key={p.templateId}
                                        color='green'
                                        style={{
                                            fontSize: 14,
                                            marginLeft: 5,
                                            marginTop: 5,
                                            marginBottom: 5,
                                            color: p.usePrice === false ? 'var(--main-red)' : '#000000',
                                        }}
                                    >
                                        <span style={{ fontWeight: 600 }}>{p.templateName}</span>
                                        <span> : {p.boxQty}</span>
                                        {p.comment && (
                                            <span style={{ marginLeft: 5 }}>
                                                <Comment comment={p.comment} size='middle' />
                                            </span>
                                        )}
                                    </Tag>
                                </Tooltip>
                            );
                        });
                    }
                },
            },
            {
                title: 'Итого ($)',
                width: 120,
                align: 'right',
                onCell: () => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return (
                        <>
                            <Flex gap='small' align='center' justify='end' style={{ color: record.totalPrice <= 0 ? 'red' : '' }}>
                                {record.status == BillStatus.Paid && (
                                    <Tooltip title='Оплачен' color='var(--main-green)'>
                                        <CheckOutlined style={{ fontSize: 18, color: 'var(--main-green)' }} />
                                    </Tooltip>
                                )}
                                {record.totalPrice && toFinanceString(record.totalPrice, 2)}
                                {record.isTotalPriceManual && (
                                    <Tooltip title='Счет изменен вручную'>
                                        <FormOutlined />
                                    </Tooltip>
                                )}
                            </Flex>
                            {record.totalPriceFinal > 0 && (
                                <>
                                    {record.discount && (
                                        <Tooltip title='Cкидка' placement='left'>
                                            <div style={{ color: 'var(--main-green)', padding: 3, marginTop: 5 }}>
                                                {toFinanceString(-1 * record.discount, 2)}
                                            </div>
                                        </Tooltip>
                                    )}
                                    {record.totalPriceFinal != record.totalPrice && (
                                        <Tooltip title='Со скидкой' placement='left'>
                                            <div style={{ background: '#d2dcd8', padding: 3, marginTop: 5 }}>
                                                {toFinanceString(record.totalPriceFinal, 2)}
                                            </div>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                ref={tableRef}
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'checkbox',
                    columnWidth: 32,
                }}
                columns={columns}
                dataSource={bills}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                onRow={(record: IBill) => {
                    return {
                        onClick: () => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                pagination={false}
                virtual={true}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
                footer={() => (
                    <span
                        style={{
                            fontWeight: 600,
                        }}
                    >
                        <span>
                            Кол-во мест:
                            <span style={{ marginLeft: 5 }}>{consignmentTotals?.totalBoxQty}</span>/
                            <span
                                style={{
                                    color: consignmentTotals?.totalBoxQty == processedTotals.totalBoxQty ? 'green' : 'red',
                                    marginLeft: 2,
                                    marginRight: 2,
                                }}
                            >
                                {processedTotals.totalBoxQty}
                            </span>
                        </span>
                        <span style={{ marginLeft: 15 }}>
                            Объемный вес:
                            <span style={{ marginLeft: 5 }}>{consignmentTotals?.totalVolumeWeight.toFixed(2)}</span>/
                            <span
                                style={{
                                    color:
                                        consignmentTotals?.totalVolumeWeight.toFixed(2) == processedTotals.totalVolumeWeight.toFixed(2)
                                            ? 'green'
                                            : 'red',
                                    marginLeft: 2,
                                    marginRight: 2,
                                }}
                            >
                                {processedTotals.totalVolumeWeight.toFixed(2)}
                            </span>
                        </span>
                        <span style={{ marginLeft: 15 }}>
                            Перелет ($):
                            <span style={{ marginLeft: 5, color: 'var(--main-green)' }}>
                                {toFinanceString(processedTotals.totalFlightPriceUsd, 2)}
                            </span>
                        </span>

                        <span style={{ marginLeft: 15 }}>
                            Скидка ($):
                            <span style={{ marginLeft: 5, color: 'var(--main-green)' }}>
                                {toFinanceString(processedTotals.totalDiscountUsd, 2)}
                            </span>
                        </span>

                        <span style={{ marginLeft: 15 }}>
                            Итого ($):
                            <span style={{ marginLeft: 5 }}>{toFinanceString(processedTotals?.totalPriceUsd, 2)}</span>
                        </span>
                    </span>
                )}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 290px - ${filterHeight}px)` }}>
            <FormHeader
                title={
                    <>
                        Машина "{truck?.number} ({truckStatusLabel(truck?.status)})" - Тарифы
                    </>
                }
            />
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            <Modal
                title='Массовое редактирование тарифа'
                width={650}
                open={bulkTariffOpen}
                okText='ОК'
                onOk={() => bulkTariffForm.submit()}
                onCancel={() => {
                    bulkTariffForm.resetFields();

                    setBulkTariffOpen(false);
                }}
            >
                <Form
                    colon={false}
                    labelCol={{ span: 5 }}
                    wrapperCol={{ span: 19 }}
                    onFinish={onTariffUpdate}
                    form={bulkTariffForm}
                    style={{ marginTop: 20 }}
                >
                    <Form.Item label='Расчетный город'>
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name='setCityRequired' style={{ width: 30 }} valuePropName='checked'>
                                <Checkbox />
                            </Form.Item>
                            <Form.Item
                                name='cityId'
                                rules={[{ required: setBulkCityRequired, message: 'Выберите город' }]}
                                style={{ width: 'calc(100% - 30px)' }}
                            >
                                <Select
                                    disabled={!setBulkCityRequired}
                                    showSearch
                                    allowClear
                                    filterOption={(input, option) =>
                                        (option?.label as string).toLowerCase().startsWith(input.toLowerCase())
                                    }
                                    filterSort={(a, b) =>
                                        (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())
                                    }
                                    options={cities.map((c) => {
                                        return { value: c.id, label: c.name };
                                    })}
                                ></Select>
                            </Form.Item>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item label='Тариф'>
                        <Flex gap='middle'>
                            <Form.Item name='flightPrice'>
                                <InputNumber
                                    decimalSeparator=','
                                    addonAfter={<AirplaneIcon />}
                                    min={0}
                                    step='0.1'
                                    stringMode
                                    precision={2}
                                />
                            </Form.Item>
                            <Form.Item name='truckPrice'>
                                <InputNumber decimalSeparator=',' addonAfter={<TruckIcon />} min={0} step='0.1' precision={2} />
                            </Form.Item>
                        </Flex>
                    </Form.Item>
                </Form>
            </Modal>

            {currentBill && editTariffModalOpen && (
                <BillModal
                    bill={currentBill}
                    airCargoCarriers={airCargoCarriers}
                    cities={cities}
                    items={countryItems.filter((i) => i.countryId == currentBill.countryId)}
                    countries={countries}
                    tariffSettings={tariffSettings}
                    onSave={onTariffUpdate}
                    onCancel={() => setEditTariffModalOpen(false)}
                />
            )}

            {currentBill?.boxGroupId && openTransferModal && (
                <TransferBoxes
                    boxGroupId={currentBill.boxGroupId}
                    items={countryItems.filter((i) => i.countryId == currentBill.countryId)}
                    countries={countries}
                    packages={currentBill?.packages}
                    onSave={() => {
                        setRefreshRequired(true);
                        setOpenTransferModal(false);
                    }}
                    onCancel={() => setOpenTransferModal(false)}
                    api={api}
                    splitConsignment={true}
                    serverFetch={serverFetch}
                />
            )}

            <Modal
                destroyOnHidden={true}
                width={1050}
                title={
                    <>
                        Добавить упаковку. (
                        <span
                            style={{
                                color:
                                    packages.map((e) => e.boxQty || 0).reduce((sum, a) => sum + a, 0) == currentBill?.qty ? 'green' : 'red',
                            }}
                        >
                            {packages.map((e) => e.boxQty || 0).reduce((sum, a) => sum + a, 0)}
                        </span>
                        / {currentBill?.qty})
                    </>
                }
                open={openPackageModal}
                okText='ОК'
                closable={false}
                onOk={() => {
                    const counts = packages.reduce(
                        (acc, item) => {
                            const key = item.templateId;
                            acc[key] = (acc[key] || 0) + 1;
                            return acc;
                        },
                        {} as Record<string | number, number>,
                    );

                    const duplicates = Math.max(...Object.values(counts));

                    if (duplicates > 1) {
                        error(api, `Коробки содержат дублирующие виды упаковок. Удалите повторы перед сохранением!`);
                        return false;
                    }

                    onSavePackages();
                }}
                onCancel={() => {
                    setCurrentBill(undefined);
                    setSelectedIds([]);

                    setOpenPackageModal(false);
                }}
            >
                <Packages
                    dataSource={packages}
                    onChange={(packages) => {
                        setPackages(packages);
                    }}
                    api={api}
                />
            </Modal>

            <Modal
                width={450}
                title={`Изменить тариф для AWB "${currentBill?.awbNumber}"`}
                open={awbOpen}
                okText='ОК'
                onOk={() => awbForm.submit()}
                onCancel={() => {
                    awbForm.resetFields();
                    setAwbOpen(false);
                }}
            >
                <Form
                    colon={false}
                    labelCol={{ span: 5 }}
                    wrapperCol={{ span: 19 }}
                    onFinish={onAwbTariffUpdate}
                    form={awbForm}
                    style={{ marginTop: 20 }}
                >
                    <Form.Item name='awbNumber' hidden={true}>
                        <Input />
                    </Form.Item>
                    <Form.Item label='Тариф' name='flightPrice'>
                        <InputNumber decimalSeparator=',' addonAfter={<AirplaneIcon />} min={0} step='0.1' stringMode />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                width={400}
                title='Причина удаления счета'
                open={openDeleteModal}
                okText='ОК'
                closable={false}
                onOk={() => {
                    deletelForm.submit();
                }}
                onCancel={() => {
                    deletelForm.resetFields();
                    setOpenDeleteModal(false);
                }}
            >
                <Form colon={false} wrapperCol={{ span: 24 }} form={deletelForm} onFinish={onDelete}>
                    <Form.Item name='reason' required rules={[{ required: true, message: 'Укажите причину удаления' }]}>
                        <TextArea rows={6} />
                    </Form.Item>
                </Form>
            </Modal>

            {contextHolder}
            {modalContextHolder}
        </div>
    );
};

export default Bills;
